import { useEffect, useRef } from "react";

/**
 * useSwipeGesture — attaches touch handlers to <body> to detect horizontal
 * swipes and drive a sidebar drawer.
 *
 * Behaviour:
 *   • Swipe FROM the left edge (first 24px) to the right → onOpen()
 *   • Swipe LEFT anywhere while drawer is open → onClose()
 *   • Only active on touch devices below the `mdBreakpoint` viewport width
 *   • Ignores swipes that start on horizontally-scrollable elements
 *     (Kanban board, tables, code blocks) marked with
 *     `data-swipe-ignore="true"` or `overflow-x-auto/scroll`.
 *
 * Design notes:
 *   • `touchstart` is passive so it never blocks scroll.
 *   • Once we determine the gesture is a horizontal swipe (delta_x > delta_y
 *     and > 16px), we call `preventDefault` on subsequent moves to avoid
 *     the page rubber-banding while the drawer follows the finger.
 *   • The threshold + velocity check prevents accidental swipes from
 *     firing on tap/scroll.
 */
export function useSwipeGesture({ open, onOpen, onClose, mdBreakpoint = 768 }) {
  const stateRef = useRef({
    startX: 0,
    startY: 0,
    startT: 0,
    active: false,
    horizontal: false,
    fromEdge: false,
  });

  // Keep the latest handlers in refs so the effect body stays stable.
  const openRef = useRef(onOpen);
  const closeRef = useRef(onClose);
  const openStateRef = useRef(open);
  useEffect(() => {
    openRef.current = onOpen;
    closeRef.current = onClose;
    openStateRef.current = open;
  }, [open, onOpen, onClose]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia(`(min-width: ${mdBreakpoint}px)`).matches) return;
    if (!("ontouchstart" in window)) return;

    const EDGE_ZONE = 24; // px from left edge to count as edge swipe
    const MIN_DIST = 60; // px horizontal distance to trigger open/close
    const MIN_VELOCITY = 0.25; // px/ms
    const AXIS_LOCK = 16; // once |dx| > this AND |dx| > |dy|, we lock horizontal

    // Detect if the touch started inside an element that scrolls horizontally.
    const inScrollableAncestor = (el) => {
      let n = el;
      while (n && n !== document.body) {
        if (n instanceof Element) {
          if (n.getAttribute("data-swipe-ignore") === "true") return true;
          const style = getComputedStyle(n);
          const ox = style.overflowX;
          if (ox === "auto" || ox === "scroll") {
            if (n.scrollWidth > n.clientWidth + 1) return true;
          }
        }
        n = n.parentNode;
      }
      return false;
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      const target = e.target;
      // Ignore swipes originating in horizontally-scrollable content —
      // e.g. Kanban board columns, chat message rows, code blocks.
      if (target && inScrollableAncestor(target) && !openStateRef.current) {
        stateRef.current.active = false;
        return;
      }
      stateRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        startT: Date.now(),
        active: true,
        horizontal: false,
        fromEdge: t.clientX <= EDGE_ZONE,
      };
    };

    const onTouchMove = (e) => {
      const s = stateRef.current;
      if (!s.active || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - s.startX;
      const dy = t.clientY - s.startY;

      if (!s.horizontal) {
        if (Math.abs(dx) > AXIS_LOCK && Math.abs(dx) > Math.abs(dy)) {
          // Horizontal intent. Only claim the gesture when it originates from
          // the edge (to open) or when the drawer is already open (to close);
          // otherwise let the browser handle it normally.
          if (s.fromEdge || openStateRef.current) {
            s.horizontal = true;
          } else {
            s.active = false;
          }
        } else if (Math.abs(dy) > AXIS_LOCK) {
          // Vertical scroll — abandon.
          s.active = false;
        }
      }

      if (s.horizontal && e.cancelable) e.preventDefault();
    };

    const onTouchEnd = (e) => {
      const s = stateRef.current;
      if (!s.active || !s.horizontal) {
        stateRef.current.active = false;
        return;
      }
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) {
        stateRef.current.active = false;
        return;
      }
      const dx = t.clientX - s.startX;
      const dt = Math.max(1, Date.now() - s.startT);
      const velocity = Math.abs(dx) / dt;

      if (!openStateRef.current && s.fromEdge && dx > MIN_DIST && velocity > MIN_VELOCITY) {
        openRef.current?.();
      } else if (openStateRef.current && dx < -MIN_DIST && velocity > MIN_VELOCITY) {
        closeRef.current?.();
      }
      stateRef.current.active = false;
    };

    // touchmove needs to be non-passive so we can preventDefault mid-gesture.
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [mdBreakpoint]);
}
